import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { updateArtistProfile, checkUserAvailabilityForAuth } from "services/api/user.service";
import { publicApiFetch } from "../../services/api/client";
import { PLATFORMS } from "../../constants/platforms";
import CountryCodeDropdown from "../UI/CountryCodeDropdown";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { COUNTRY_CODES } from "../../constants/countryCodes";

const ConfirmAccountDetailsForm: React.FC = () => {
  const navigate = useNavigate();
  const [artistName, setArtistName] = useState("");
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [distributors, setDistributors] = useState<string[]>([]); // Array of distributor names
  const [selectedDistributorDropdown, setSelectedDistributorDropdown] = useState(""); // Selected value from dropdown
  const [customDistributorInput, setCustomDistributorInput] = useState(""); // Input for custom distributor when "Other" is selected
  const [mobileNumber, setMobileNumber] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState("+1");
  const [artistNameError, setArtistNameError] = useState("");
  const [spotifyUrlError, setSpotifyUrlError] = useState("");
  const [distributorError, setDistributorError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mobileAvailabilityStatus, setMobileAvailabilityStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const { userProfile, refreshProfile, isProfileComplete, loading } = useUser();

  // Redirect to dashboard if profile is already complete
  // Only redirect after profile has been loaded and verified complete
  useEffect(() => {
    // Don't redirect while loading or if profile doesn't exist yet
    if (loading || !userProfile) {
      return;
    }

    // Only redirect if profile is truly complete
    if (isProfileComplete) {
      navigate('/dashboard', { replace: true });
    } else {
      // Profile incomplete, show form
    }
  }, [isProfileComplete, navigate, loading, userProfile]);

  // Prepopulate form fields with existing data
  useEffect(() => {
    if (userProfile && !loading) {
      // Prepopulate mobile number and extract country code
      if (userProfile.mobile) {
        const mobile = userProfile.mobile;
        //console.log("ConfirmAccountDetails - Original mobile:", mobile); // Debug log
        
        let countryCode = "+1";
        let mobileNumber = "";
        
        // Handle different mobile number formats - dynamically extract all country codes
        if (mobile.startsWith('+')) {
          // Mobile starts with +, extract country code by matching against known codes
          // Try longest codes first (3 digits, then 2, then 1) to avoid false matches
          const allDigits = mobile.substring(1).replace(/\D/g, '');
          
          let matched = false;
          
          // Try 3-digit country codes first (e.g., +212, +213, +216, +220, etc.)
          if (allDigits.length >= 10) {
            const code3 = '+' + allDigits.substring(0, 3);
            if (COUNTRY_CODES.some(cc => cc.code === code3)) {
              countryCode = code3;
              mobileNumber = allDigits.substring(3);
              matched = true;
            }
          }
          
          // Try 2-digit country codes if 3-digit didn't match (e.g., +20, +27, +30, +44, +84, etc.)
          if (!matched && allDigits.length >= 9) {
            const code2 = '+' + allDigits.substring(0, 2);
            if (COUNTRY_CODES.some(cc => cc.code === code2)) {
              countryCode = code2;
              mobileNumber = allDigits.substring(2);
              matched = true;
            }
          }
          
          // Try 1-digit country codes if 2-digit didn't match (e.g., +1, +7)
          if (!matched && allDigits.length >= 8) {
            const code1 = '+' + allDigits.substring(0, 1);
            if (COUNTRY_CODES.some(cc => cc.code === code1)) {
              countryCode = code1;
              mobileNumber = allDigits.substring(1);
              matched = true;
            }
          }
          
          // If no match found, default to using all digits as mobile number
          if (!matched) {
            mobileNumber = allDigits;
          }
        } else if (mobile.length >= 10) {
          // Mobile doesn't start with +, try to infer country code
          // If starts with 1 and length is 11, likely US/Canada
          if (mobile.startsWith('1') && mobile.length === 11) {
            countryCode = '+1';
            mobileNumber = mobile.substring(1);
          } else {
            // No country code prefix, use full number
            mobileNumber = mobile.replace(/\D/g, '');
          }
        } else {
          // Short number, treat as mobile number only
          mobileNumber = mobile.replace(/\D/g, '');
        }
        
        //console.log("ConfirmAccountDetails - Final country code:", countryCode); // Debug log
        //console.log("ConfirmAccountDetails - Final mobile number:", mobileNumber); // Debug log
        
        setSelectedCountryCode(countryCode);
        setMobileNumber(mobileNumber);
      }

      // Prepopulate artist data from user_artists table
      if (userProfile.artist && userProfile.artist.length > 0) {
        const artistData = userProfile.artist[0];
        
        if (artistData.artist_name) {
          setArtistName(artistData.artist_name);
        }
        
        if (artistData.spotify_url) {
          setSpotifyUrl(artistData.spotify_url);
        }
        
        if (artistData.distributor) {
          // Parse distributors from comma-separated string
          const distributorValue = artistData.distributor;
          let initialDistributors: string[] = [];
          
          if (distributorValue) {
            // Split by comma and trim each value
            const distributorArray = distributorValue.split(',').map(d => d.trim()).filter(d => d.length > 0);
            
            // Normalize each distributor (check if it matches a platform name case-insensitively)
            initialDistributors = distributorArray.map(dist => {
              const matchedPlatform = PLATFORMS.find(p => p.name.toLowerCase() === dist.toLowerCase());
              return matchedPlatform ? matchedPlatform.name : dist; // Use platform name if matched, otherwise keep original
            });
          }
          
          setDistributors(initialDistributors);
        }
      }
    }
  }, [userProfile, loading]);

  
  // Pure validation functions that don't update state
  const isValidArtistName = (name: string): boolean => {
    return name.trim().length > 0;
  };

  const isValidSpotifyUrl = (url: string): boolean => {
    if (!url.trim()) return true;
    const urlPattern = /^https:\/\/open\.spotify\.com\/artist\/[a-zA-Z0-9]+.*$/;
    const uriPattern = /^spotify:artist:[a-zA-Z0-9]+$/;
    return urlPattern.test(url.trim()) || uriPattern.test(url.trim());
  };

  const isValidMobileNumber = (mobile: string): boolean => {
    if (!mobile.trim()) return false;
    const cleanNumber = mobile.replace(/\D/g, '');
    // Updated validation: 10-12 digits
    if (cleanNumber.length < 10 || cleanNumber.length > 12) return false;
    
    // If mobile matches user's current mobile, it's valid
    const fullMobileNumber = `${selectedCountryCode}${mobile}`;
    if (userProfile?.mobile && fullMobileNumber === userProfile.mobile) {
      return true;
    }
    
    // Check if mobile is available (not taken, and not currently checking)
    if (mobileAvailabilityStatus === 'taken') return false;
    if (mobileAvailabilityStatus === 'checking') return false;
    // Only valid if explicitly available (don't allow submission if status is idle/unchecked)
    return mobileAvailabilityStatus === 'available';
  };

  const isValidDistributor = (distributors: string[]): boolean => {
    return distributors.length > 0;
  };

  // Validation functions that update state
  const validateArtistName = (name: string): boolean => {
    if (!name.trim()) {
      setArtistNameError("Artist Name is required");
      return false;
    }
    setArtistNameError("");
    return true;
  };

  const validateSpotifyUrl = (url: string): boolean => {
    if (!url.trim()) {
      setSpotifyUrlError("");
      return true;
    }
    const trimmed = url.trim();
    const urlPattern = /^https:\/\/open\.spotify\.com\/artist\/[a-zA-Z0-9]+.*$/;
    const uriPattern = /^spotify:artist:[a-zA-Z0-9]+$/;
    if (!urlPattern.test(trimmed) && !uriPattern.test(trimmed)) {
      setSpotifyUrlError("Enter a Spotify artist link with artist ID (e.g. https://open.spotify.com/artist/4Z8W4fKeB5YxbusRsdQVPb or spotify:artist:4Z8W4fKeB5YxbusRsdQVPb).");
      return false;
    }
    setSpotifyUrlError("");
    return true;
  };

  const checkMobileAvailability = async (mobile: string, countryCode?: string) => {
    const cleanNumber = mobile.replace(/\D/g, '');
    if (!cleanNumber || cleanNumber.length < 10 || cleanNumber.length > 12) {
      setMobileAvailabilityStatus('idle');
      return;
    }

    const codeToUse = countryCode || selectedCountryCode;
    const fullMobileNumber = `${codeToUse}${mobile}`;
    setMobileAvailabilityStatus('checking');
    try {
      // For confirm account details, check if mobile exists for any user
      const response = await checkUserAvailabilityForAuth("mobile", fullMobileNumber);
      
      if (response.exists) {
        // Check if the found user is the current user by comparing emails or mobile
        if (userProfile?.mobile && response.mobile === userProfile.mobile) {
          // It's the current user's mobile, so it's available to them
          setMobileAvailabilityStatus('available');
          setMobileError("");
        } else {
          // It's someone else's mobile
          setMobileAvailabilityStatus('taken');
          setMobileError("Mobile number is already registered");
        }
      } else {
        // Mobile is available
        setMobileAvailabilityStatus('available');
        setMobileError("");
      }
    } catch (error) {
      console.error("Error checking mobile availability:", error);
      setMobileAvailabilityStatus('idle');
      setMobileError("Error checking mobile availability");
    }
  };

  // Debounced mobile check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const cleanNumber = mobileNumber.replace(/\D/g, '');
      if (cleanNumber && cleanNumber.length >= 10 && cleanNumber.length <= 12) {
        checkMobileAvailability(cleanNumber);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [mobileNumber, selectedCountryCode]);

  const validateMobileNumber = (mobile: string): boolean => {
    if (!mobile.trim()) {
      setMobileError("Mobile number is required");
      return false;
    }
    
    // Remove all non-digit characters for validation
    const cleanNumber = mobile.replace(/\D/g, '');
    
    // Updated validation: 10-12 digits
    if (cleanNumber.length < 10) {
      setMobileError("Mobile number must be at least 10 digits");
      return false;
    }
    
    if (cleanNumber.length > 12) {
      setMobileError("Mobile number cannot exceed 12 digits");
      return false;
    }

    // Check if mobile is taken
    if (mobileAvailabilityStatus === 'taken') {
      return false;
    }
    
    setMobileError("");
    return true;
  };

  const validateDistributor = (distributors: string[]): boolean => {
    if (distributors.length === 0) {
      setDistributorError("At least one distributor is required");
      return false;
    }
    
    setDistributorError("");
    return true;
  };

  // Add distributor function
  const addDistributor = (distributor: string) => {
    if (!distributor.trim()) return;
    
    const trimmedDistributor = distributor.trim();
    
    // Check if already exists (case-insensitive)
    if (distributors.some(d => d.toLowerCase() === trimmedDistributor.toLowerCase())) {
      toast.error("Distributor already added");
      return;
    }
    
    // Normalize: check if it matches a platform name (case-insensitive)
    const matchedPlatform = PLATFORMS.find(p => p.name.toLowerCase() === trimmedDistributor.toLowerCase());
    const normalizedDistributor = matchedPlatform ? matchedPlatform.name : trimmedDistributor;
    
    setDistributors([...distributors, normalizedDistributor]);
    setSelectedDistributorDropdown(""); // Reset dropdown
    setCustomDistributorInput(""); // Reset custom input
    if (distributorError) validateDistributor([...distributors, normalizedDistributor]); // Re-validate
  };

  // Remove distributor function
  const removeDistributor = (index: number) => {
    const newDistributors = distributors.filter((_, i) => i !== index);
    setDistributors(newDistributors);
    if (distributorError) validateDistributor(newDistributors); // Re-validate
  };

  // Handle distributor selection from dropdown
  const handleDistributorSelect = (value: string) => {
    if (value === "Other") {
      // Don't add "Other" directly, wait for custom input
      setSelectedDistributorDropdown(value);
    } else if (value) {
      addDistributor(value);
    }
  };

  // Handle adding custom distributor
  const handleAddCustomDistributor = () => {
    if (customDistributorInput.trim()) {
      addDistributor(customDistributorInput.trim());
    } else {
      toast.error("Please enter a distributor name");
    }
  };

  const isFormValid = (): boolean => {
    return isValidArtistName(artistName) && isValidSpotifyUrl(spotifyUrl) && isValidDistributor(distributors) && isValidMobileNumber(mobileNumber);
  };

  const handleContinue = async () => {
    if (!isFormValid()) {
      return;
    }
    
    // Validate token before submission
    try {
      const { getAuthToken } = await import("../../utils/getAuthToken");
      await getAuthToken(true); // Force refresh to check validity before save
    } catch (error: any) {
      // Token validation failed - error handling in getAuthToken will trigger logout
      console.error("[ConfirmAccountDetails] Token validation failed before submit:", error);
      return; // Stop submission
    }
    
    setSubmitting(true);
    try {
      // Update user profile with the provided details
      // skipArtistId: true - This is account setup for logged-in user, not artist-scoped
      await updateArtistProfile({
        firstname: userProfile?.firstname || "",
        lastname: userProfile?.lastname || "",
        artistname: artistName,
        spotifyUrl: spotifyUrl,
        distributor: distributors.join(', '), // Join distributors array with comma and space
        mobile: `${selectedCountryCode}${mobileNumber}`,
        newPassword: "",
        verificationCode: "",
        skipArtistId: true, // Don't send X-Artist-Id header for confirm-details (account setup)
      });
      
      //Show toast success/error message based on response
      toast.success('Profile updated successfully');
      
      // Wait a brief moment for backend to process the update
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh profile once - the refreshProfile function now prevents concurrent calls
      await refreshProfile();
      
      // Navigate to dashboard
      // Note: If profile is still incomplete, the redirect logic in App.tsx or UserContext
      // will handle redirecting back to confirm-account-details if needed
      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      
      // Handle 400 Bad Request errors - show the error message from response
      if (error?.status === 400 && error?.message) {
        toast.error(error.message, {
          toastId: "confirm-account-error-400",
          autoClose: 5000,
        });
        setSubmitting(false);
        return;
      }
      
      // Handle database connection errors (500) - don't logout, just show error
      if (error?.status === 500 && error?.isDatabaseError) {
        toast.error("Database connection error. Please try again.", {
          toastId: "database-error",
          autoClose: 5000,
        });
        setSubmitting(false);
        return;
      }
      
      // Handle 401 Unauthorized errors (real auth failures)
      if (error?.status === 401 || error?.isUnauthorized || (error?.message && error.message.includes("Unauthorized"))) {
        // Use centralized logout handler (prevents race conditions)
        const { performLogout } = await import("../../utils/logoutHandler");
        await performLogout("401 Unauthorized from confirm account details");
        return;
      }
      
      toast.error(error.message || 'Failed to update profile. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading while profile is being fetched
  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-nova-color">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleContinue();
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center px-4 sm:px-6 bg-nova-color">
      <form onSubmit={handleFormSubmit} className="w-full max-w-md flex flex-col items-center space-y-6 p-6 bg-nova-color login-form mt-8 sm:mt-32">
        
        {/* Logo */}
        <div className="flex-shrink-0">
          <img src="/assets/images/Nova.png" alt="Nova" className="h-10 w-auto mb-6 sm:mb-8" />
        </div>

        {/* Welcome Message */}
        <div className="text-center mb-6" style={{ marginBottom: "2.5rem" }}>
        {/* Artist Name */}
          <p className="text-gray-700 text-sm-[1rem] mb-1">Welcome back </p>
          <p className="text-gray-500 text-sm-[0.85rem]">Confirm account details to continue</p>
        </div>

        {/* Artist Name */}
        <div className="w-full sm:w-[25.8125rem]">
          <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
            ARTIST NAME
          </label>
          <input
            type="text"
            placeholder="Enter your artist name"
            className={`w-full border-b ${artistNameError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans`}
            style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
            value={artistName}
            onChange={(e) => {
              const value = e.target.value;
              setArtistName(value);
              if (artistNameError) {
                validateArtistName(value);
              }
            }}
            onBlur={() => validateArtistName(artistName)}
          />
          {artistNameError && (
            <p className="text-red-500 text-xs mt-1">{artistNameError}</p>
          )}
        </div>

        {/* Spotify URL (optional) */}
        <div className="w-full sm:w-[25.8125rem]">
          <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
            SPOTIFY URL (OPTIONAL)
          </label>
          <input
            type="text"
            placeholder="Enter your Spotify artist profile URL (optional)"
            className={`w-full border-b ${spotifyUrlError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans`}
            style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
            value={spotifyUrl}
            onChange={(e) => {
              setSpotifyUrl(e.target.value);
              if (spotifyUrlError) {
                validateSpotifyUrl(e.target.value);
              }
            }}
            onBlur={() => validateSpotifyUrl(spotifyUrl)}
          />
          <Link to="https://support.spotify.com/us/artists/article/finding-your-artist-track-release-links/" target="_blank" style={{ fontSize: "0.7rem" }} className="text-sm text-[#939393] hover:underline mt-1 block">
            Where can I find this?
          </Link>
          {spotifyUrlError && <p className="text-red-500 text-xs mt-1">{spotifyUrlError}</p>}
        </div>

        {/* Distributors - Multiple Selection with Tags */}
        <div className="w-full sm:w-[25.8125rem]">
          <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
            DISTRIBUTORS
          </label>
          
          {/* Display existing distributors as tags */}
          {distributors.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {distributors.map((distributor, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-900 rounded-full text-sm font-family-messina-sans"
                >
                  {distributor}
                  <button
                    type="button"
                    onClick={() => removeDistributor(index)}
                    className="ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={`Remove ${distributor}`}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}
          
          {/* Add Distributor - Dropdown Only */}
          <div className="space-y-3">
            <select
              value={selectedDistributorDropdown}
              onChange={(e) => handleDistributorSelect(e.target.value)}
              className={`w-full border-b ${distributorError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none font-family-messina-sans`}
              style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties}
            >
              <option value="">Select distributor to add</option>
              {PLATFORMS.filter(platform => 
                platform.name !== "Other" && 
                !distributors.some(d => d.toLowerCase() === platform.name.toLowerCase())
              ).map((platform) => (
                <option key={platform.id} value={platform.name}>
                  {platform.name}
                </option>
              ))}
              <option value="Other">Other</option>
            </select>
            
            {/* Custom Distributor Input - Only show when "Other" is selected */}
            {selectedDistributorDropdown === "Other" && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customDistributorInput}
                  onChange={(e) => setCustomDistributorInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomDistributor();
                    }
                  }}
                  placeholder="Enter custom distributor name"
                  className={`flex-1 border-b ${distributorError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none font-family-messina-sans`}
                  style={{ backgroundColor: '#F8F7F2' } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={handleAddCustomDistributor}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedDistributorDropdown("");
                    setCustomDistributorInput("");
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          
          {distributorError && <p className="text-red-500 text-xs mt-1">{distributorError}</p>}
          {distributors.length === 0 && (
            <p className="text-gray-500 text-xs mt-2">No distributors added yet</p>
          )}
        </div>

        {/* Mobile Number */}
        <div className="w-full sm:w-[25.8125rem]">
          <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
            MOBILE NUMBER
          </label>
          <div className="flex gap-2">
            {/* Country Code Dropdown */}
            <CountryCodeDropdown
              value={selectedCountryCode}
              onChange={(value) => {
                setSelectedCountryCode(value);
                // Re-check mobile availability when country code changes with new country code
                const cleanNumber = mobileNumber.replace(/\D/g, '');
                if (cleanNumber && cleanNumber.length >= 10 && cleanNumber.length <= 12) {
                  checkMobileAvailability(cleanNumber, value);
                }
              }}
              className="flex-shrink-0"
              style={{ 
                backgroundColor: '#F8F7F2',
                minWidth: '120px'
              }}
            />
            
            {/* Mobile Number Input */}
            <input
              type="tel"
              placeholder="Enter your mobile number"
              className={`flex-1 border-b ${mobileError ? 'border-red-500' : 'border-gray-300'} px-0 py-2 text-sm focus:outline-none placeholder:font-normal font-family-messina-sans`}
              style={{ backgroundColor: '#F8F7F2', '--tw-placeholder-color': '#939393' } as React.CSSProperties}
              value={mobileNumber}
              onChange={(e) => {
                // Remove all non-digit characters and limit to 12 digits
                const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
                setMobileNumber(digits);
                if (mobileError) {
                  setMobileError("");
                }
              }}
              onBlur={() => validateMobileNumber(mobileNumber)}
              maxLength={12}
            />
          </div>
          {mobileError && <p className="text-red-500 text-xs mt-1">{mobileError}</p>}
        </div>

        {/* Continue Button */}
        <button
          type="submit"
          className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition flex items-center justify-center gap-3 uppercase min-h-[3rem] disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={submitting || !isFormValid()}
        >
          {submitting ? (
            <>
              <div className="h-5 w-5 animate-spin border-2 border-t-transparent border-gray-600 rounded-full" />
              <span>CONTINUING...</span>
            </>
          ) : (
            "CONTINUE"
          )}
        </button>

        {/* Contact Link */}
        <div className="w-full text-center mt-2">
          <Link to="mailto:contact@marsmarkets.com?subject=Contact from Mars Website" className="text-[#939393] hover:underline" style={{ fontSize: "0.7rem" }}>
            Questions? Contact Us
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ConfirmAccountDetailsForm;
